import React, { useEffect } from "react";
import {
  CrfCheckItemSpec,
  CrfItemAvailabilitySpec,
  CrfPageSectionAvailabilitySpec,
  EdcGroupId,
  InitialItemSequence,
  StudySpec,
} from "maven-lib/dist/consts/edc";
import { isAvailableFor } from "maven-lib/dist/consts/edc/lib";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { classNames } from "maven-lib/dist/utils/misc";
import { HbCheck } from "@maven-surface/components/base/HbCheck";

interface QuestionnaireCheckItemProps {
  studySpec?: StudySpec;
  questionnaire: EcoaQuestionnaire;
  item: CrfCheckItemSpec;
  folderId?: string;
  visitId?: string;
  visitSeq?: number;
  crfPageId?: string;
  crfPageSeq?: number;
  disabled?: boolean;
  isAvailable?: boolean;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  onValueChange: (values: Array<{ itemId: string | null; dbVal: string | number | null }>) => void;
}

export function QuestionnaireCheckItem({
  studySpec,
  questionnaire,
  item,
  disabled,
  folderId,
  visitId,
  visitSeq,
  crfPageId,
  crfPageSeq,
  isAvailable,
  defaultItemValues,
  onValueChange,
}: QuestionnaireCheckItemProps) {
  const { codes } = item.itemCode;

  const getIsAvailable = (
    items: Array<{ itemId: string | null; dbVal: string | number | null }>,
    availability: CrfItemAvailabilitySpec | CrfPageSectionAvailabilitySpec[]
  ): boolean => {
    if (!studySpec || !folderId || !visitId || !visitSeq || !crfPageId || !crfPageSeq) return true;

    return isAvailableFor(
      studySpec,
      [],
      items.map(({ itemId, dbVal }) => ({
        id: BigInt(0),
        itemId: itemId || "",
        dbVal,
        sectionId: questionnaire.item.sectionId,
        itemSeq: questionnaire.item.original.itemSeq || InitialItemSequence,
        groupId: EdcGroupId,
        folderId,
        visitId,
        visitSeq,
        crfPageId,
        crfPageSeq,
        calcVal: null,
        freshman: false,
        deleted: false,
      })),
      availability,
      { groupId: EdcGroupId, folderId, visitId, visitSeq, crfPageId, crfPageSeq }
    );
  };

  const getUpdatedItemValues = (items: Array<{ itemId: string | null; dbVal: string | number | null }>) => {
    const updatedItemValues = [...items];
    codes.forEach((code) => {
      if (code?.availability) {
        if (!getIsAvailable(updatedItemValues, code.availability)) {
          const idx = updatedItemValues.findIndex(({ itemId }) => itemId === code.id);
          if (idx > -1) updatedItemValues[idx] = { itemId: updatedItemValues[idx].itemId, dbVal: null };

          if (code.typeText) {
            const idx = updatedItemValues.findIndex(({ itemId }) => itemId === code?.typeText?.id);
            if (idx > -1) updatedItemValues[idx] = { itemId: updatedItemValues[idx].itemId, dbVal: null };
          }
        }
      }
    });

    return updatedItemValues;
  };

  const itemDbVals = (() => {
    if (!defaultItemValues || !defaultItemValues.length) return new Array(codes.length);

    return getUpdatedItemValues(defaultItemValues);
  })();

  const handleChangeValues = (
    values: Array<{
      itemId: string | null;
      dbVal: string | number | null;
    }>
  ) => {
    return onValueChange(getUpdatedItemValues(values));
  };

  useEffect(() => {
    if (!isAvailable && defaultItemValues?.some(({ dbVal }) => dbVal || typeof dbVal === "number") && !item.ecoa?.noSkipWhenDisabled)
      onValueChange(defaultItemValues.map(({ itemId }) => ({ itemId, dbVal: null })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, defaultItemValues, onValueChange]);

  return (
    <div
      className={classNames(
        "hb-check",
        disabled && `--disabled`,
        `--direction-${String(item.direction).toLowerCase()}`,
        `--appearance-${String(item.direction === "HORIZONTAL" ? "BORDERED" : "BULLET").toLowerCase()}`
      )}
    >
      <div className="hb-check-items">
        {codes.map((code, index) => {
          const isAvailableItem = code?.availability ? getIsAvailable(itemDbVals, code.availability) : true;

          return (
            <HbCheck
              key={index}
              disabled={disabled || !isAvailableItem}
              item={{
                id: code.id,
                val: code.dbVal,
                text: code.uiVal || "-",
                icon: code.icon,
                typeText: code.typeText,
              }}
              onValueChange={handleChangeValues}
              currentValuesAll={itemDbVals}
            />
          );
        })}
      </div>
    </div>
  );
}
